import { Link, useParams } from 'react-router';
import { Card, LoadingRegion, formatMoneyOrFree } from '@gatecheckpro/design-system';
import { useResource } from '../../lib/useResource.ts';
import { FailureNotice } from '../../lib/refusal.tsx';
import { useEventWorkspace } from './EventWorkspace.tsx';

interface VersionRow {
  readonly version: number;
  readonly displayName: string;
  readonly priceMinor: string;
  readonly currency: string;
  readonly securityMode: string;
  readonly createdAt: string;
  readonly supersededAt: string | null;
}

/**
 * Every version of a ticket's terms.
 *
 * Kept visible because the invariant it demonstrates is one an organizer needs
 * to trust: a price change does not rewrite what somebody already agreed to.
 * When orders exist in Slice 3, each will point at exactly one of these rows.
 */
export function TicketVersionsPage() {
  const { event, organizationId } = useEventWorkspace();
  const { ticketProductId } = useParams<{ ticketProductId: string }>();
  const { resource } = useResource<{ versions: VersionRow[] }>(
    ticketProductId === undefined
      ? null
      : `/api/v1/organizer/organizations/${organizationId}/events/${event.eventId}/ticket-products/${ticketProductId}/versions`,
  );

  const product = event.ticketProducts.find(
    (candidate) => candidate.ticketProductId === ticketProductId,
  );

  if (resource.status === 'loading') return <LoadingRegion label="Loading versions" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  return (
    <div className="gc-stack gc-stack--loose">
      <div className="gc-stack gc-stack--tight">
        <span className="gc-eyebrow">
          <Link to="../tickets">Tickets &amp; capacity</Link>
        </span>
        <h3>{product?.code ?? 'Ticket'} — every version of these terms</h3>
        <p className="gc-muted">
          Terms are never edited. Revising creates a version and supersedes the last one, so a
          ticket sold under old terms keeps them.
        </p>
      </div>

      <Card flush>
        <div className="gc-table-scroll" style={{ border: 0, borderRadius: 0 }}>
          <table className="gc-table">
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col">Name</th>
                <th scope="col">Price</th>
                <th scope="col">Credential</th>
                <th scope="col">In force</th>
              </tr>
            </thead>
            <tbody>
              {resource.data.versions.map((version) => (
                <tr key={version.version}>
                  <td className="gc-mono">v{version.version}</td>
                  <td>{version.displayName}</td>
                  <td>
                    {formatMoneyOrFree({
                      minor: Number(version.priceMinor),
                      currency: version.currency,
                    })}
                  </td>
                  <td className="gc-muted">
                    {version.securityMode === 'SECURE_PASS' ? 'Secure pass' : 'Standard pass'}
                  </td>
                  <td className="gc-muted gc-mono">
                    {new Date(version.createdAt).toLocaleString()}
                    {version.supersededAt === null
                      ? ' — current'
                      : ` → ${new Date(version.supersededAt).toLocaleString()}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
